import React from 'react';
import { observer, inject } from 'mobx-react';
import { withTranslation } from 'react-i18next';
import 'antd/dist/antd.css';
import './App.less';
import { Router, Route, Redirect, Switch } from 'react-router-dom';
import Spinner, { SpinnerOverlayContent } from 'Components/Spinner';
import routes, {
  AUTHORIZED_ROUTES_WL,
  FORCE_REDIRECT_TO_SIGNUP,
} from 'common/constants/routing.constants';
import asyncComponent from 'Components/AsyncComponent';
import NotFound from 'pages/NotFound';
import SignInComp from 'pages/Auth/SignInComp';
import './i18n';
import history from 'customHistory';
import { ConfirmEmailPage } from 'pages/Auth/ConfirmEmailPage';
import { ConfirmPassResetPage } from 'pages/Auth/ConfirmPassResetPage';
import { SuccessPassResetPage } from 'pages/Auth/SuccessPassResetPage';
import { Analytics } from 'Components/Analytics/Analytics';
import { SignedWithAuth } from 'pages/Auth/PostSignIn';
import { VerifyEmailPage } from 'pages/Auth/VerifyEmailPage';
import { SuccessEmailVerifiedPage } from 'pages/Auth/SuccessEmailVerifiedPage';
import { VerifyEmailSentPage } from 'pages/Auth/VerifyEmailSentPage';
import { YMInitializer } from 'react-yandex-metrika';
import { Modals } from './Modals';
import AppBody from './AppBody';
import { appConfig } from './config';
import { ExternalLoginPage } from './pages/Auth/ExternalLogin';
import { ExternalSignUpPage } from './pages/Auth/ExternalSignUp';
import withTracker from './withTracker';
import { IS_DISONA } from './common/utils/style.util';

const Accounts = asyncComponent(() => import('pages/Accounts'));
const Trading = asyncComponent(() => import('pages/Trading'));
const BalanceOperations = asyncComponent(() =>
  import('pages/BalanceOperations')
);
const AccountOperation = asyncComponent(() => import('pages/AccountOperation'));
const AccountOpen = asyncComponent(() => import('pages/AccountOpen'));
const Profile = asyncComponent(() => import('pages/Profile'));
const AnalyticsPage = asyncComponent(() => import('pages/Analytics'));
const Partners = asyncComponent(() => import('pages/Partners'));
const CreateUTM = asyncComponent(() => import('pages/Partners/CreateUTM'));
const Bonus = asyncComponent(() => import('pages/Bonus'));
const Ewallet = asyncComponent(() => import('pages/Ewallet'));
const Kyc = asyncComponent(() => import('pages/Kyc'));
const FAQ = asyncComponent(() => import('pages/FAQ'));

const TradingSessionsMap = asyncComponent(() =>
  import('pages/TradingSessionsMap/components/TradingSessionsMap')
);
const OperationDetails = asyncComponent(() =>
  import('pages/AccountOperation/OperationDetails/OperationDetails')
);
const Wizard = asyncComponent(() => import('pages/Wizard/Wizard'));
const CopyTrading = asyncComponent(() =>
  import('pages/CopyTrading/CopyTrading')
);
const Rewards = asyncComponent(() => import('pages/Rewards/Rewards'));
const PaymentRedirect = asyncComponent(() => import('pages/PostPayment'));
const __DesignSystem = asyncComponent(() => import('pages/__DesignSystem'));

const App = ({ AuthStore, AppStore }) => {
  if (!AuthStore.isLoaded || !AppStore.isI18nextInitiated) {
    return (
      <SpinnerOverlayContent>
        <Spinner />
      </SpinnerOverlayContent>
    );
  }
  const { pathname, search } = window.location;
  if (!AUTHORIZED_ROUTES_WL.includes(pathname)) {
    if (!AuthStore.session.is_authorised) {
      if (IS_DISONA && process.env.REACT_APP_ENVIRONMENT === 'production') {
        window.location.replace('https://disona.com/login');
        return null;
      }
      AppStore.setInitialUrl(pathname);

      const url = new URLSearchParams(search);
      if (FORCE_REDIRECT_TO_SIGNUP.includes(pathname)) {
        if (!url.has('isLogin')) {
          url.append('isLogin', 'false');
        }
      }
      return (
        <Router history={history}>
          <Redirect
            to={{
              pathname: '/login',
              search: url.toString(),
            }}
          />
          <Route path="/login" component={SignInComp} />
        </Router>
      );
    }
    history.push(history.location);
  }

  return (
    <AppBody>
      <YMInitializer
        accounts={[Number(process.env.REACT_APP_YM_ID)]}
        options={{ webvisor: true }}
        version="2"
      />
      <Modals />
      <Router history={history}>
        <Switch>
          <Redirect exact from="/" to={routes.trading.path} />
          <Route
            path={routes.wizard.path}
            exact
            component={withTracker(Wizard)}
          />
          <Route path={routes.kyc.path} exact component={withTracker(Kyc)} />
          <Route
            path={routes.trading.path}
            exact
            component={withTracker(Trading)}
          />
          <Route
            path={routes.accounts.path}
            exact
            component={withTracker(Accounts)}
          />
          <Route
            path={routes.operations.path}
            exact
            component={withTracker(BalanceOperations)}
          />
          <Route
            path={routes.profile.path}
            exact
            component={withTracker(Profile)}
          />
          <Route
            path={routes.ewallet.path}
            exact
            component={withTracker(Ewallet)}
          />
          <Route
            path={routes.bonus.path}
            exact
            component={withTracker(Bonus)}
          />
          <Route
            path={routes.partnership.path}
            exact
            component={withTracker(Partners)}
          />
          <Route
            path={routes.createUTM.path}
            exact
            component={withTracker(CreateUTM)}
          />
          <Route path={routes.faq.path} exact component={withTracker(FAQ)} />
          <Route
            path={routes.analytics.path}
            exact
            component={withTracker(AnalyticsPage)}
          />
          <Route
            path={routes.rewards.path}
            exact
            component={withTracker(Rewards)}
          />
          <Route
            path={routes.copyTrading.path}
            exact
            component={withTracker(CopyTrading)}
          />
          <Route
            path={routes.accountOperations.path}
            exact
            component={withTracker(AccountOperation)}
          />
          <Route
            path={routes.operationDetails.path}
            exact
            component={withTracker(OperationDetails)}
          />
          <Route
            path={routes.accountOpen.path}
            exact
            component={withTracker(AccountOpen)}
          />
          <Route
            path={routes.auth.postLogin.path}
            component={withTracker(SignedWithAuth)}
            exact
          />
          <Route
            path={routes.postPayment.path}
            component={withTracker(PaymentRedirect)}
            exact
          />
          <Route
            path={routes.auth.login.path}
            component={withTracker(SignInComp)}
            exact
          />
          <Route
            path={routes.auth.postRegistration.path}
            component={withTracker(SignedWithAuth)}
            exact
          />
          <Route
            path={routes.external.registration.path}
            component={withTracker(ExternalSignUpPage)}
            exact
          />
          <Route
            path={routes.external.login.path}
            component={withTracker(ExternalLoginPage)}
            exact
          />
          <Route
            path={routes.auth.confirmEmail.postSent.path}
            component={withTracker(VerifyEmailSentPage)}
            exact
          />
          <Route
            path={routes.auth.confirmEmail.postConfirm.path}
            component={withTracker(SuccessEmailVerifiedPage)}
            exact
          />
          <Route
            path={routes.auth.confirmEmail.doAction.path}
            component={withTracker(VerifyEmailPage)}
            exact
          />
          <Route
            path={routes.auth.confirmPassReset.postSent.path}
            component={withTracker(ConfirmEmailPage)}
            exact
          />
          <Route
            path={routes.auth.confirmPassReset.postConfirm.path}
            component={withTracker(SuccessPassResetPage)}
            exact
          />
          <Route
            path={routes.auth.confirmPassReset.doAction.path}
            component={withTracker(ConfirmPassResetPage)}
            exact
          />
          <Route
            path={routes.external.analytics.path}
            component={withTracker(Analytics)}
            exact
          />
          <Route
            path={routes.external.tradingSessionsMap.path}
            component={withTracker(TradingSessionsMap)}
            exact
          />
          {appConfig.isLocal && (
            <Route
              path={routes.__dev.designSystem.path}
              component={__DesignSystem}
              exact
            />
          )}
          <Route component={NotFound} />
        </Switch>
      </Router>
    </AppBody>
  );
};

export default withTranslation()(
  inject('AuthStore', 'AppStore', 'ModalStore')(observer(App))
);
